import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import PoliticianCard from '../components/PoliticianCard';
import { fetcher, API_BASE } from '../services/api';

export default function SearchPage () {
  const [searchParams, setSearchParams] = useSearchParams();
  const [nlpQuery, setNlpQuery] = useState(searchParams.get('q') || '');
  const [nlpResults, setNlpResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);
  const [debugError, setDebugError] = useState<string | null>(null);

  const party = searchParams.get('party');
  const state = searchParams.get('state');
  const position = searchParams.get('position');
  const search = searchParams.get('q');

  // Build query string
  const queryParams = new URLSearchParams();
  if (party) queryParams.set('party', party);
  if (state) queryParams.set('state', state);
  if (position) queryParams.set('position', position);
  if (search && !nlpQuery) queryParams.set('search', search);
  queryParams.set('per_page', '20');

  const { data, isLoading, error } = useSWR(`politicians?${queryParams.toString()}`, fetcher);

  // Debug logging
  console.log('[SearchPage] SWR key:', `politicians?${queryParams.toString()}`);
  console.log('[SearchPage] Data:', data);
  console.log('[SearchPage] Error:', error);
  console.log('[SearchPage] IsLoading:', isLoading);

  useEffect(() => {
    const runDebugFetch = async () => {
      try {
        const res = await fetch(`${API_BASE}/politicians?per_page=6`);
        const json = await res.json();
        setDebugData({ status: res.status, data: json });
        setDebugError(null);
      } catch (err: any) {
        setDebugError(err.message || 'fetch failed');
        setDebugData(null);
      }
    };

    runDebugFetch();
  }, []);

  const handleNlpSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nlpQuery.trim()) return;

    setIsSearching(true);
    setSearchParams({ q: nlpQuery });

    try {
      const response = await fetch(`${API_BASE}/search/nlp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: nlpQuery }),
      });
      const data = await response.json();
      setNlpResults(data.data || []);
    } catch (error) {
      console.error('NLP Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* NLP Search */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Búsqueda</h1>
        <form onSubmit={handleNlpSearch} className="flex gap-2">
          <input
            type="text"
            value={nlpQuery}
            onChange={(e) => setNlpQuery(e.target.value)}
            placeholder="Pregunta en lenguaje natural, ej: ¿Qué senadores han faltado más este año?"
            className="input flex-1"
          />
          <button type="submit" className="btn btn-primary" disabled={isSearching}>
            {isSearching ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
      </div>

      {/* NLP Results */}
      {nlpResults.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Resultados de búsqueda inteligente</h2>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-purple-800">
              La IA interpretó tu pregunta y encontró {nlpResults.length} resultados relevantes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nlpResults.map((result: any) => (
              <PoliticianCard
                key={result.id}
                id={result.id}
                name={result.full_name}
                photoUrl={result.photo_url}
                partyName={result.party_name}
                partyAbbrev={result.party_abbrev}
                positionTitle={result.position_title}
                stateName={result.state_name}
                rating={result.rating || 75}
                stats={{
                  initiatives: result.stats?.initiatives || 0,
                  attendance: result.attendance?.percentage || 0,
                  approvals: 0,
                  speeches: 0,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular Results */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          {search ? `Resultados para "${search}"` : 'Todos los representantes'}
        </h2>

        {error ? (
          <div className="py-16 text-center text-red-600">
            Error loading politicians: {error.message}
          </div>
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-40 bg-gray-200 rounded-lg" />
              </div>
            ))}
          </div>
        ) : data?.data?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.data.map((p: any) => (
              <PoliticianCard
                key={p.id}
                id={p.id}
                name={p.full_name}
                photoUrl={p.photo_url}
                partyName={p.party_name}
                partyAbbrev={p.party_abbrev}
                partyColor={p.party_color}
                positionTitle={p.position_title}
                stateName={p.state_name}
                rating={p.rating || 75}
                stats={{
                  initiatives: p.stats?.initiatives || 0,
                  attendance: p.attendance?.percentage || 0,
                  approvals: 0,
                  speeches: 0,
                }}
              />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center text-gray-500">No se encontró ningún representante.</div>
        )}

        {/* Pagination */}
        {data?.meta?.total_pages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button className="btn btn-outline text-sm">Anterior</button>
              <span className="text-sm text-gray-600">
                Página {data.meta.page} de {data.meta.total_pages}
              </span>
              <button className="btn btn-outline text-sm">Siguiente</button>
            </nav>
          </div>
        )}
      </div>

      {/* DEBUG */}
      <div className="fixed bottom-4 right-4 bg-white border border-gray-300 rounded-xl p-4 max-w-sm text-xs z-50 shadow-lg">
        <div className="font-bold mb-2">DEBUG API</div>
        <div>API_BASE: {API_BASE}</div>
        <div>Endpoint: {API_BASE}/politicians?per_page=6</div>
        {debugError ? (
          <div className="text-red-600 mt-2">Error: {debugError}</div>
        ) : debugData ? (
          <div className="mt-2 text-green-600">
            Status: {debugData.status}<br />
            Count: {debugData.data?.data?.length ?? 0}
          </div>
        ) : (
          <div className="mt-2 text-gray-500">Cargando debug...</div>
        )}
      </div>
    </div>
  );
}
